import ReactMarkdown, { type Components } from 'react-markdown';
import remarkGfm from 'remark-gfm';
import clsx from 'clsx';

/**
 * Renders question stems, options and explanations that are authored as
 * lightweight Markdown (paragraphs separated by blank lines, `-`/`*` bullet
 * lists, GitHub-flavoured pipe tables, `**bold**`, inline `code`, links).
 *
 * Why this exists: the corpus stores real `\n\n` paragraph breaks and pipe
 * tables inside plain-text columns. Rendered as a bare `<p>{text}</p>` the
 * browser collapses every newline to a space, so multi-part scenarios and
 * resource tables turn into one unreadable wall of text. This component turns
 * that stored Markdown into structured, readable HTML.
 *
 * Security: raw HTML in the source is NOT rendered (no `rehype-raw`), and
 * react-markdown's default URL transform strips dangerous protocols
 * (`javascript:` …). So untrusted content cannot inject markup or scripts.
 */

interface MarkdownContentProps {
  content: string;
  /** Extra classes applied to the wrapper; children inherit colour/size/weight. */
  className?: string;
  /**
   * Inline mode for phrasing-only contexts (e.g. an answer <button>): paragraphs
   * render as <span> instead of <p> so the DOM stays valid and text flows inline.
   */
  inline?: boolean;
}

/** Block-level component map — structural spacing + Tailwind styling only. */
const blockComponents: Components = {
  p: ({ children }) => <p className="leading-relaxed">{children}</p>,
  strong: ({ children }) => <strong className="font-semibold">{children}</strong>,
  ul: ({ children }) => <ul className="list-disc space-y-1 pl-5">{children}</ul>,
  ol: ({ children }) => <ol className="list-decimal space-y-1 pl-5">{children}</ol>,
  li: ({ children }) => <li className="leading-relaxed">{children}</li>,
  h1: ({ children }) => <h1 className="text-lg font-bold">{children}</h1>,
  h2: ({ children }) => <h2 className="text-base font-bold">{children}</h2>,
  h3: ({ children }) => <h3 className="text-base font-semibold">{children}</h3>,
  blockquote: ({ children }) => (
    <blockquote className="border-l-4 border-slate-300 pl-4 italic text-slate-600 dark:border-white/20 dark:text-slate-400">
      {children}
    </blockquote>
  ),
  hr: () => <hr className="border-slate-200 dark:border-white/10" />,
  code: ({ children }) => (
    <code className="rounded bg-slate-100 px-1 py-0.5 font-mono text-[0.9em] dark:bg-white/10">
      {children}
    </code>
  ),
  pre: ({ children }) => (
    <pre className="overflow-x-auto rounded-lg bg-slate-100 p-3 text-sm dark:bg-white/10">
      {children}
    </pre>
  ),
  a: ({ children, href }) => (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="font-medium text-primary hover:underline"
    >
      {children}
    </a>
  ),
  table: ({ children }) => (
    <div className="overflow-x-auto">
      <table className="w-full border-collapse text-sm">{children}</table>
    </div>
  ),
  th: ({ children }) => (
    <th className="border border-slate-300 bg-slate-100 px-3 py-2 text-left font-semibold dark:border-white/15 dark:bg-white/5">
      {children}
    </th>
  ),
  td: ({ children }) => (
    <td className="border border-slate-300 px-3 py-2 align-top dark:border-white/15">
      {children}
    </td>
  ),
};

/** Inline map: paragraphs become spans so markup is valid inside phrasing contexts. */
const inlineComponents: Components = {
  ...blockComponents,
  p: ({ children }) => <span>{children}</span>,
};

export default function MarkdownContent({ content, className, inline = false }: MarkdownContentProps) {
  const Wrapper = inline ? 'span' : 'div';
  return (
    <Wrapper className={clsx(inline ? 'markdown-inline' : 'space-y-3', className)}>
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={inline ? inlineComponents : blockComponents}
      >
        {content}
      </ReactMarkdown>
    </Wrapper>
  );
}
